import {
  getAdminclassMentorRoleList,
  getRoleList,
  getRoleListForHeader,
} from '@/utils/data'
import { atom, createStore } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { ActivityUser, User } from 'packages/sdks-next/growp'

import { semesterInfoAtom } from './atom'
import {
  REFLECTION_KEY,
  ACTIVITY_REFLECTION_KEY,
  X_RBAC_TOKEN_KEY,
  LAST_SET_X_TOKEN_TIME_KEY,
  SCHOOL_ID_KEY,
  ACTIVITY_SCHOOL_ID_KEY,
  ACTIVITY_ID_KEY,
  IS_ADMIN_KEY,
  USER_THEME_COLOR,
  ADMIN_THEME_COLOR,
  SELECTED_ROLE_KEY,
  ADMINCLASS_MENTOR_GRADUATES_TERM,
} from './const'
import { SelectedRoleEnum } from './types'
import {
  getParseLocalValue,
  getBooleanLocalValue,
  getNumberLocalValue,
  getThemeLocalValue,
} from './utils'

export const store = createStore()

const reflection = getParseLocalValue(REFLECTION_KEY)
export const reflectionStorage = atomWithStorage<User | undefined>(
  REFLECTION_KEY,
  reflection
)

const xRbacToken = getParseLocalValue(X_RBAC_TOKEN_KEY)
export const xRbacTokenStorage = atomWithStorage<string | undefined>(
  X_RBAC_TOKEN_KEY,
  xRbacToken
)

const lastSetXTokenTime = getParseLocalValue(LAST_SET_X_TOKEN_TIME_KEY)
export const lastSetXTokenTimeStorage = atomWithStorage<string | undefined>(
  LAST_SET_X_TOKEN_TIME_KEY,
  lastSetXTokenTime
)

const schoolId = getNumberLocalValue(SCHOOL_ID_KEY)
export const schoolIdStorage = atomWithStorage<number | undefined>(
  SCHOOL_ID_KEY,
  // 默认学校 id 为 3
  schoolId || 3
)

const activityReflection = getParseLocalValue(ACTIVITY_REFLECTION_KEY)
export const activityReflectionStorage = atomWithStorage<
  ActivityUser | undefined
>(ACTIVITY_REFLECTION_KEY, activityReflection)

const isAdmin = getBooleanLocalValue(IS_ADMIN_KEY)
export const isAdminStorage = atomWithStorage<boolean>(IS_ADMIN_KEY, isAdmin)

const activityId = getNumberLocalValue(ACTIVITY_ID_KEY)
export const activityIdStorage = atomWithStorage<number | undefined>(
  ACTIVITY_ID_KEY,
  activityId
)

const localUserThemeColor = getThemeLocalValue(USER_THEME_COLOR)
export const userThemeColorAtom = atomWithStorage<string>(
  USER_THEME_COLOR,
  localUserThemeColor
)

const localAdminThemeColor = getThemeLocalValue(ADMIN_THEME_COLOR)
export const adminThemeColorAtom = atomWithStorage<string>(
  ADMIN_THEME_COLOR,
  localAdminThemeColor
)

const activitySchoolId = getNumberLocalValue(ACTIVITY_SCHOOL_ID_KEY)
export const activitySchoolIdStorage = atomWithStorage<number | undefined>(
  ACTIVITY_SCHOOL_ID_KEY,
  activitySchoolId
)

const selected_role = getParseLocalValue(SELECTED_ROLE_KEY)
export const selectedRoleStorage = atomWithStorage<
  SelectedRoleEnum | undefined
>(SELECTED_ROLE_KEY, selected_role)

export const selectedRoleAtom = atom(
  (get) => {
    const storedRole = get(selectedRoleStorage)
    const semesterInfo = get(semesterInfoAtom)
    const roleKeys = semesterInfo
      ? getRoleList(semesterInfo).map((r) => r.key)
      : []

    if (!roleKeys.length) return undefined

    // 如果本地存储的角色有效，就用它
    if (storedRole && roleKeys.includes(storedRole)) {
      return storedRole
    }

    // 否则，自动选择第一个可用角色
    return roleKeys[0]
  },
  (get, set, newRole: SelectedRoleEnum) => {
    set(selectedRoleStorage, newRole) // 更新存储
  }
)

const adminclass_mentor_graduates_term = getParseLocalValue(
  ADMINCLASS_MENTOR_GRADUATES_TERM
)
export const adminclassMentorGraduatesTermStorage = atomWithStorage<
  string | undefined
>(ADMINCLASS_MENTOR_GRADUATES_TERM, adminclass_mentor_graduates_term)

export const adminclassMentorGraduatesTermAtom = atom(
  (get) => {
    const storedTerm = get(adminclassMentorGraduatesTermStorage)
    const semesterInfo = get(semesterInfoAtom)
    const roleKeys = semesterInfo
      ? getRoleList(semesterInfo).map((r) => r.key)
      : []

    if (!roleKeys.length) return undefined

    if (semesterInfo?.is_adminclass_mentor !== 1) return undefined

    const { adminclassTermNames } = getAdminclassMentorRoleList(semesterInfo)

    // 如果只是行政班导师
    if (roleKeys.length === 1) {
      // 如果本地存储的角色在行政班导师列表中，就用它
      if (storedTerm && adminclassTermNames.includes(storedTerm)) {
        return storedTerm
      }

      return adminclassTermNames[0]
    }

    const headerRoleListKeys = getRoleListForHeader(semesterInfo).map(
      (r) => r.key
    )

    if (storedTerm) {
      // 如果存在的 term 在当前角色行政班导师列表里
      if (
        headerRoleListKeys.includes(
          `${SelectedRoleEnum.AdminclassMentor}-${storedTerm}`
        )
      ) {
        return storedTerm
      } else {
        return headerRoleListKeys[0]
      }
    }

    return undefined
  },
  (get, set, newTerm: string | undefined) => {
    set(adminclassMentorGraduatesTermStorage, newTerm) // 更新存储
  }
)
